import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { ParseResult } from "../../utils/parsers";
import { styles } from "./style";

interface TranslationStatusProps extends WithStyles<typeof styles> {
	isProcessing?: boolean;
	parseResults?: ParseResult | null;
	validationErrors?: string[];
}

const Component: React.FC<TranslationStatusProps> = ({
	classes = {},
	isProcessing = false,
	parseResults = null,
	validationErrors = []
}) => {
	const theme = Mui.useTheme();

	// Calculate status metrics
	const hasData = parseResults && parseResults.totalRecords > 0;
	const hasErrors = parseResults && (parseResults.errors.length > 0 || validationErrors.length > 0);
	const hasWarnings = parseResults && parseResults.warnings.length > 0;
	const queryStringRecords = parseResults ? 
		parseResults.data.filter(record => record.queryString && String(record.queryString).includes('=')).length : 0;

	// Status color logic
	const getStatusColor = (): 'success' | 'warning' | 'error' | 'info' => {
		if (isProcessing) return 'info';
		if (hasErrors) return 'error';
		if (hasWarnings) return 'warning';
		if (hasData) return 'success';
		return 'info';
	};

	const getStatusIcon = () => {
		const color = getStatusColor();
		switch(color) {
			case 'success': return <Icons.CheckCircle />;
			case 'warning': return <Icons.AlertTriangle />;
			case 'error': return <Icons.XCircle />;
			case 'info': return <Icons.Info />;
			default: return <Icons.Info />;
		}
	};

	const getStatusText = (): string => {
		if (isProcessing) {
			return 'Processing...';
		}
		if (!parseResults) {
			return 'Ready';
		}
		if (hasErrors) {
			return `${parseResults.totalRecords} records, ${parseResults.errors.length + validationErrors.length} errors`;
		}
		if (hasWarnings) {
			return `${parseResults.totalRecords} records, ${parseResults.warnings.length} warnings`;
		}
		if (hasData) {
			return `${parseResults.totalRecords} records processed, ${queryStringRecords} with queryString`;
		}
		return 'No data';
	};

	return (
		<Mui.Box 
			display="flex" 
			alignItems="center" 
			py={1} 
			px={2} 
			bgcolor="grey.50" 
			borderRadius={1}
			className={classes.container}
		>
			<FeatherIcon 
				className={classes.icon}
				style={{ color: theme.palette[getStatusColor()].main }}
			>
				{getStatusIcon()}
			</FeatherIcon>
			
			<Mui.Typography variant="body2" className={classes.statusText}>
				<strong>Market Processing:</strong> {getStatusText()}
			</Mui.Typography>

			{isProcessing && (
				<Mui.CircularProgress size={16} className={classes.spinner} />
			)}
		</Mui.Box>
	);
};

export const TranslationStatus = withStyles(Component, styles);